import { useEffect, useState, useCallback } from 'react';
import { Folder } from 'lucide-react';
import type { Poll } from '@yawi/types';
import { ApiError } from '@yawi/infrastructure';
import { useServices, useToast } from '@/app/providers';
import { useI18n } from '@/shared/i18n';
import { Loading } from '@/core/ui/components/Loading';
import { PollCard } from '../components/PollCard';
import { PollResults } from '../components/PollResults';

type TabType = 'active' | 'closed';

export const PollsPage = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { pollRepo, systemSettings, currentUser } = useServices();
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [activePolls, setActivePolls] = useState<Poll[]>([]);
  const [closedPolls, setClosedPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [closedLoaded, setClosedLoaded] = useState(false);

  const votingMode = systemSettings?.pollVotingMode;
  const userUnits = currentUser?.units;

  const loadActivePolls = useCallback(async () => {
    try {
      const polls = await pollRepo.getActivePolls();
      setActivePolls(polls);
    } catch (e) {
      console.error('Error loading active polls:', e);
      toast.error(t('common.error'));
    }
  }, [pollRepo, toast, t]);

  const loadClosedPolls = useCallback(async () => {
    try {
      const polls = await pollRepo.getClosedPolls();
      setClosedPolls(polls);
      setClosedLoaded(true);
    } catch (e) {
      console.error('Error loading closed polls:', e);
      toast.error(t('common.error'));
    }
  }, [pollRepo, toast, t]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadActivePolls();
      setLoading(false);
    };
    init();
  }, [loadActivePolls]);

  useEffect(() => {
    if (activeTab === 'closed' && !closedLoaded) {
      setLoading(true);
      loadClosedPolls().finally(() => setLoading(false));
    }
  }, [activeTab, closedLoaded, loadClosedPolls]);

  const handleVote = async (pollId: string, optionIds: string[], unitId?: string) => {
    try {
      await pollRepo.vote(pollId, { optionIds, unitId });
      toast.success(t('polls.voteSuccess'));
      await loadActivePolls();
    } catch (e) {
      console.error('Error voting:', e);
      if (ApiError.isApiError(e) && e.code) {
        switch (e.code) {
          case 'POLL_11001':
            toast.error(t('polls.error.onlyTitulares'));
            break;
          case 'POLL_11002':
            toast.error(t('polls.error.unitRequired'));
            break;
          case 'POLL_11003':
            toast.error(t('polls.error.unitNotBelongsToUser'));
            break;
          case 'POLL_11004':
            toast.error(t('polls.error.pollNotActive'));
            break;
          case 'POLL_11005':
            toast.error(t('polls.error.pollNotFound'));
            break;
          default:
            toast.error(e.message || t('polls.error.generic'));
        }
      } else {
        toast.error(t('polls.error.generic'));
      }
    }
  };

  const currentPolls = activeTab === 'active' ? activePolls : closedPolls;
  const emptyMessage = activeTab === 'active' ? t('polls.empty') : t('polls.emptyClosed');

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('polls.title')}</h2>
          <p className="text-slate-500 text-sm">Participa en las encuestas activas de la comunidad.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'active'
              ? 'bg-violet-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {t('polls.tabs.active')}
        </button>
        <button
          onClick={() => setActiveTab('closed')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'closed'
              ? 'bg-violet-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {t('polls.tabs.closed')}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <Loading />
      ) : currentPolls.length === 0 ? (
        <div className="text-center p-12 border-2 border-dashed border-slate-200 rounded-xl">
          <Folder size={48} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">{emptyMessage}</p>
        </div>
      ) : activeTab === 'active' ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {currentPolls.map((poll) => (
            <PollCard
              key={poll.id}
              poll={poll}
              onVote={handleVote}
              votingMode={votingMode}
              userUnits={userUnits}
            />
          ))}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {currentPolls.map((poll) => (
            <div key={poll.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-2">{poll.title}</h3>
              {poll.description && (
                <p className="text-sm text-slate-500 mb-4">{poll.description}</p>
              )}
              <PollResults
                poll={poll}
                showVotedBadge={poll.hasVoted}
                showClosedBadge={!poll.hasVoted}
                userVotedOptionIds={poll.userVotedOptionIds}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
